import type { Feature } from '@/app/components/search/components/EvolutionCard/utils/facilitiesTypes.ts';
import { waitingItem } from '@/app/components/search/components/EvolutionCard/utils/facilitiesTypes.ts';
import { getParams } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/getFeaturesParams.ts';
import type { GetFeaturesParams } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/types.ts';
import { getTransportFeatures } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getTransportFeatures.tsx';
import { getDateFeatures } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getDateFeatures.tsx';
import { getSoldOutFeatures } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getSoldOutFeatures.tsx';
import { getMealFeatures } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getMealFeatures.tsx';
import { getBaggageFeatures } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getBaggageFeatures.tsx';
import { getTransferFeatures } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getTransferFeatures.tsx';
import { getCancellationFeature } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getCancellationFeature.tsx';
import { getFacilitiesFeatures } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getFacilitiesFeatures.tsx';

type GetFeaturesOutput = {
  features?: Feature[];
};

const withGridSide = <T extends Feature>(features: T[], side: Feature['gridSide']) =>
  features.map((f) => ({ ...f, gridSide: side })) as Array<T & { gridSide?: Feature['gridSide'] }>;

const withOrder = <T extends Feature>(features: T[], order: number) =>
  features.map((f) => ({ ...f, order })) as Array<T & { order: number }>;

export const getFeatures = (props: GetFeaturesParams): GetFeaturesOutput => {
  const params = getParams(props);
  const { isLoading, isWaiting, isOverlay, soldOut, dealCard, isDPFlexible } = params;
  const hasAccommodationData = dealCard?.product?.accommodation;
  const showLoaders = isLoading || isWaiting || !hasAccommodationData;

  // when the card is loading, we show loaders instead of USPs
  if (showLoaders) {
    return {
      features: isOverlay
        ? []
        : [
            ...withGridSide([{ ...waitingItem, loaderWidth: 208, loaderHeight: 16 }], 'left'),
            ...withGridSide([{ ...waitingItem, loaderWidth: 168, loaderHeight: 16 }], 'left'),
            ...withGridSide([{ ...waitingItem, loaderWidth: 208, loaderHeight: 16 }], 'right'),
            ...withGridSide([{ ...waitingItem, loaderWidth: 168, loaderHeight: 16 }], 'right'),
          ],
    };
  }

  // when the deal is sold out, we show only the sold out USP
  if (soldOut) {
    return {
      features: getSoldOutFeatures(params),
    };
  }

  return {
    // these USPs are shown vertically in the card
    features: [
      ...withGridSide(getDateFeatures(params), 'left'),
      ...withGridSide(isDPFlexible || isOverlay ? [] : getMealFeatures(params), 'left'),
      ...withGridSide(getCancellationFeature(params), 'left'),
      ...withGridSide(withOrder(getTransferFeatures(params), 1), 'left'),
      ...withGridSide(getTransportFeatures(params), 'right'),
      ...withGridSide(getBaggageFeatures(params), 'right'),
      // HO has no transport or baggage, so the facilities the TSX card shows fill the right column
      ...withGridSide(getFacilitiesFeatures(params), 'right'),
    ],
  };
};
